import { useEffect, useState } from 'react'
import { Button, Flex, Select, Typography } from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { fetchAvailableAccounts, revealAccountPassword } from './subscriberApi'

interface PoolAccountSelectProps {
  vispId: string
  value?: string
  onChange?: (login: string | undefined) => void
}

/** Kvotali VISPning bo'sh hisobi; qiymat — login. Birinchi bo'sh hisob o'zi tanlanadi. */
export function PoolAccountSelect({ vispId, value, onChange }: PoolAccountSelectProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const [search, setSearch] = useState('')

  const accounts = useQuery({
    queryKey: [...queryKeys.network.all, 'pool-picker', vispId, search],
    queryFn: () => fetchAvailableAccounts(vispId, search),
  })
  const rows = accounts.data?.rows ?? []
  const first = rows[0]?.login

  useEffect(() => {
    if (!value && !search && first) onChange?.(first)
  }, [value, search, first, onChange])

  const selectedId = rows.find((row) => row.login === value)?.id
  const reveal = useMutation({ mutationFn: (id: string) => revealAccountPassword(vispId, id) })
  const { reset } = reveal
  useEffect(() => reset(), [value, reset])

  return (
    <Flex vertical gap={4}>
      <Select
        value={value}
        onChange={onChange}
        showSearch
        filterOption={false}
        onSearch={setSearch}
        loading={accounts.isPending}
        notFoundContent={accounts.isSuccess ? t('subscriber.pool.noAccounts') : null}
        options={rows.map((row) => ({ value: row.login, label: row.login }))}
        style={{ width: '100%' }}
      />
      {accounts.isError ? <Typography.Text type="danger">{translateError(accounts.error, t)}</Typography.Text> : null}
      {can('subscriber.password.set') && selectedId ? (
        <Flex gap={8} align="center" wrap>
          <Button size="small" loading={reveal.isPending} onClick={() => reveal.mutate(selectedId)}>
            {t('subscriber.pool.showPassword')}
          </Button>
          {reveal.data ? (
            <Typography.Text code copyable>
              {reveal.data}
            </Typography.Text>
          ) : null}
          {reveal.isError ? <Typography.Text type="danger">{translateError(reveal.error, t)}</Typography.Text> : null}
        </Flex>
      ) : null}
    </Flex>
  )
}
